import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

const CACHE_KEY = 'offers_news_v2';
const CACHE_TTL = 10 * 60 * 1000;
const PAGE_SIZE = 9;

const prefixIfRelative = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `https://app.yaper.co${url.startsWith('/') ? '' : '/'}${url}`;
};

const stripHtml = (html = '') => {
  const div = document.createElement('div');
  div.innerHTML = String(html || '');
  return div.textContent || div.innerText || '';
};

const toExcerpt = (html = '', fallback = '', n = 180) => {
  const s = stripHtml(html) || String(fallback || '');
  return s.length > n ? s.slice(0, n).trim() + '…' : s;
};

const formatDate = (val) => {
  if (!val) return '';
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return String(val);
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const fetchPage = async (page) => {
  const url = `https://app.yaper.co/api/v1/content_pages?page_number=${page}&content_type=blog&source_id=3`;
  const res = await fetch(url, { cache: 'no-store' });
  if (!res.ok) return [];
  const json = await res.json();
  return Array.isArray(json?.data) ? json.data : [];
};

const normalizeItem = (b) => {
  const rawDate = new Date(b?.updated_at || b?.created_at || 0).getTime();
  return {
    id: b?.id ?? `${b?.heading || ''}__${b?.created_at || ''}`,
    title: b?.heading || b?.creditcardname || 'Untitled',
    image: prefixIfRelative(b?.preview_img || b?.banner || ''),
    date: formatDate(b?.updated_at || b?.created_at),
    rawDate: Number.isFinite(rawDate) ? rawDate : 0,
    excerpt: toExcerpt(b?.preview_text || b?.content || ''),
  };
};

const preloadBatch = (items) =>
  Promise.all(
    items
      .filter((i) => i.image)
      .map(
        (i) =>
          new Promise((resolve) => {
            const img = new window.Image();
            img.onload = resolve;
            img.onerror = resolve;
            img.src = i.image;
          })
      )
  );

const SkeletonCard = () => (
  <div className="news-card">
    <div className="skeleton-img" />
    <div className="news-card-text">
      <div className="skeleton-line short" />
      <div className="skeleton-line long" />
    </div>
  </div>
);

const NewsCard = React.memo(({ item, onClick }) => (
  <div className="news-card" onClick={onClick}>
    <div className="news-card-img-wrap">
      {item.image ? (
        <img
          src={item.image}
          alt={item.title}
          loading="eager"
          decoding="sync"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover' /* ← fills the box, no white space */,
            objectPosition: '50% 50%',
            display: 'block',
          }}
        />
      ) : (
        <div
          style={{
            width: '100%',
            height: '100%',
            background: '#e5e7eb',
            borderRadius: '1rem',
          }}
        />
      )}
    </div>
    <div className="news-card-text">
      <div className="date">{item.date}</div>
      <div className="title">{item.title}</div>
    </div>
  </div>
));

export default function OffersNewsHomeCarousel() {
  const [allItems, setAllItems] = useState([]);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [loading, setLoading] = useState(true);
  // 'idle' | 'preloading' | 'done'
  const [loadMoreState, setLoadMoreState] = useState('idle');
  const [fetchedAll, setFetchedAll] = useState(false);
  const [current, setCurrent] = useState(0);

  const seenIds = useRef(new Set());
  const preloadingRef = useRef(false); // prevent duplicate triggers
  const allItemsRef = useRef([]); // ref mirror for scroll handler
  const visibleCountRef = useRef(PAGE_SIZE);
  const fetchedAllRef = useRef(false);

  const navigate = useNavigate();
  const location = useLocation();
  const isNewsPage = location.pathname === '/offers-news';

  // Keep refs in sync
  useEffect(() => {
    allItemsRef.current = allItems;
  }, [allItems]);
  useEffect(() => {
    visibleCountRef.current = visibleCount;
  }, [visibleCount]);
  useEffect(() => {
    fetchedAllRef.current = fetchedAll;
  }, [fetchedAll]);

  // Fetch all pages in background
  const fetchAllInBackground = useCallback(async () => {
    let page = 3; // pages 1+2 already fetched
    while (true) {
      try {
        const data = await fetchPage(page);
        if (!data || data.length === 0) break;
        const normalized = [];
        for (const b of data) {
          const key = String(
            b?.id ?? `${b?.heading || ''}__${b?.created_at || ''}`
          );
          if (seenIds.current.has(key)) continue;
          seenIds.current.add(key);
          normalized.push(normalizeItem(b));
        }
        if (normalized.length > 0) {
          setAllItems((prev) => {
            const merged = [...prev, ...normalized].sort(
              (a, b) => b.rawDate - a.rawDate
            );
            allItemsRef.current = merged;
            return merged;
          });
        }
        if (data.length < 10) break;
        page++;
      } catch {
        break;
      }
    }
    setFetchedAll(true);
    fetchedAllRef.current = true;
  }, []);

  // Initial load
  useEffect(() => {
    (async () => {
      setLoading(true);

      // Try cache first
      try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < CACHE_TTL && data?.length > 0) {
            await preloadBatch(data.slice(0, PAGE_SIZE));
            data.forEach((item) => seenIds.current.add(String(item.id)));
            setAllItems(data);
            allItemsRef.current = data;
            setLoading(false);
            fetchAllInBackground().then(() => {
              localStorage.setItem(
                CACHE_KEY,
                JSON.stringify({
                  data: allItemsRef.current,
                  timestamp: Date.now(),
                })
              );
            });
            return;
          }
        }
      } catch {}

      // No cache — fetch pages 1+2
      try {
        const [p1, p2] = await Promise.all([fetchPage(1), fetchPage(2)]);
        const combined = [...p1, ...p2];
        const normalized = [];
        const freshSeen = new Set();
        for (const b of combined) {
          const key = String(
            b?.id ?? `${b?.heading || ''}__${b?.created_at || ''}`
          );
          if (freshSeen.has(key)) continue;
          freshSeen.add(key);
          normalized.push(normalizeItem(b));
        }
        normalized.sort((a, b) => b.rawDate - a.rawDate);
        await preloadBatch(normalized.slice(0, PAGE_SIZE));
        seenIds.current = freshSeen;
        setAllItems(normalized);
        allItemsRef.current = normalized;
        setLoading(false);
        fetchAllInBackground().then(() => {
          localStorage.setItem(
            CACHE_KEY,
            JSON.stringify({
              data: allItemsRef.current,
              timestamp: Date.now(),
            })
          );
        });
      } catch {
        setLoading(false);
      }
    })();
  }, [fetchAllInBackground]);

  // The core load-more logic
  const triggerLoadMore = useCallback(async () => {
    if (preloadingRef.current) return;
    const currentVisible = visibleCountRef.current;
    const all = allItemsRef.current;
    const isFetchedAll = fetchedAllRef.current;

    // Nothing more to show
    if (currentVisible >= all.length && isFetchedAll) return;

    preloadingRef.current = true;
    setLoadMoreState('preloading'); // show skeletons immediately

    const nextBatch = all.slice(currentVisible, currentVisible + PAGE_SIZE);

    if (nextBatch.length > 0) {
      // Preload images, then reveal
      await preloadBatch(nextBatch);
      setVisibleCount((prev) => prev + PAGE_SIZE);
      visibleCountRef.current = currentVisible + PAGE_SIZE;
    } else if (!isFetchedAll) {
      // Data not fetched yet — wait a bit and retry
      await new Promise((r) => setTimeout(r, 500));
      const fresh = allItemsRef.current.slice(
        currentVisible,
        currentVisible + PAGE_SIZE
      );
      if (fresh.length > 0) {
        await preloadBatch(fresh);
        setVisibleCount((prev) => prev + PAGE_SIZE);
        visibleCountRef.current = currentVisible + PAGE_SIZE;
      }
    }

    setLoadMoreState('idle');
    preloadingRef.current = false;
  }, []);

  // Scroll listener — trigger at 70% scroll
  useEffect(() => {
    if (!isNewsPage) return;

    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = docHeight > 0 ? scrollTop / docHeight : 0;

      // Trigger at 70% scroll
      if (scrollPercent >= 0.7) {
        triggerLoadMore();
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isNewsPage, triggerLoadMore]);

  // Auto-rotate carousel
  useEffect(() => {
    if (isNewsPage || allItems.length === 0) return;
    const max = Math.min(5, allItems.length);
    const t = setInterval(() => setCurrent((p) => (p + 1) % max), 5000);
    return () => clearInterval(t);
  }, [isNewsPage, allItems.length]);

  const topFive = useMemo(() => allItems.slice(0, 5), [allItems]);
  const currentItem = topFive[current] || null;
  const visibleItems = useMemo(
    () => allItems.slice(0, visibleCount),
    [allItems, visibleCount]
  );
  const hasMore = visibleCount < allItems.length || !fetchedAll;
  const isPreloading = loadMoreState === 'preloading';

  // How many skeletons to show while preloading
  const skeletonCount = Math.min(
    PAGE_SIZE,
    allItems.length - visibleCount > 0
      ? allItems.length - visibleCount
      : PAGE_SIZE
  );

  return (
    <>
      <style>{`
  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
  .skeleton-img {
    width: 100%; height: 220px; border-radius: 1rem;
    background: linear-gradient(90deg, #e0e0e0 25%, #efefef 50%, #e0e0e0 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
  }
  .skeleton-line {
    height: 14px; border-radius: 4px; margin-bottom: 8px;
    background: linear-gradient(90deg, #e0e0e0 25%, #efefef 50%, #e0e0e0 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
  }
  .skeleton-line.short { width: 40%; }
  .skeleton-line.long  { width: 85%; }

  .carousel-section { padding: 4rem 1rem; background: #eaf6ff; text-align: center; font-family: 'Titillium Web', sans-serif; }
  .carousel-section h2 { font-size: 2rem; font-weight: 700; margin-bottom: 0.5rem; }
  .carousel-section p  { font-size: 1.5rem; color: #555; margin-bottom: 4rem; }

  .carousel-wrapper { display: flex; justify-content: center; align-items: flex-end; max-width: 1100px; margin: 0 auto; position: relative; }
  .carousel-left { flex: 0 0 60%; height: 700px; border-radius: 1rem; overflow: hidden; background: transparent; display: flex; align-items: center; justify-content: center; }
  .carousel-image { width: 100%; height: 100%; border-radius: 1rem; object-fit: contain; }
  .carousel-placeholder { width: 100%; height: 100%; border-radius: 1rem; background: #ddd; display: flex; align-items: center; justify-content: center; color: #666; }

  .carousel-card { flex: 0 0 45%; background: #fff; box-shadow: 0 4px 20px rgba(0,0,0,0.1); padding: 2rem; margin-left: -80px; z-index: 2; border-radius: 1rem; height: 330px; display: flex; flex-direction: column; justify-content: space-between; text-align: left; }
  .carousel-card .date { font-size: 0.9rem; color: #888; margin-bottom: 0.5rem; }
  .carousel-card h3 { font-size: 1rem; font-weight: 700; color: #111; margin-bottom: 0.7rem; }
  .carousel-card p  { font-size: 0.95rem; color: #444; margin-bottom: 0.5rem; }
  .carousel-card button { background: #1e3a8a; color: white; border: none; padding: 0.6rem 1.2rem; border-radius: 0.4rem; cursor: pointer; font-size: 0.95rem; align-self: flex-start; }

  .carousel-dots { margin-top: 2rem; display: flex; justify-content: center; gap: 0.5rem; }
  .carousel-dot { width: 12px; height: 12px; background: #ccc; border-radius: 50%; cursor: pointer; transition: all 0.3s; }
  .carousel-dot.active { background: #1e3a8a; width: 40px; border-radius: 20px; }

  .news-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    column-gap: 1.25rem;
    row-gap: 2.5rem;
    max-width: 1100px;
    margin: 0 auto;
    width: 100%;
    box-sizing: border-box;
  }

  .news-card { cursor: pointer; transition: transform 0.2s ease; }
  .news-card:hover { transform: translateY(-5px); }

  /* ← THE ONLY NEW RULE ADDED — fixes uneven card sizes */
  .news-card-img-wrap {
    width: 100%;
    height: 220px;
    border-radius: 1rem;
    overflow: hidden;
    background: #f0f0f0;
  }

  .news-card img { width: 100%; height: 220px; object-fit: contain; border-radius: 1rem; }

  .news-card-text { text-align: left; margin-top: 1.2rem; }
  .news-card-text .date  { font-size: 0.85rem; color: #888; margin-bottom: 0.6rem; }
  .news-card-text .title { font-size: 1rem; font-weight: 600; color: #222; }

  .load-status {
    margin-top: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    color: #64748b;
    font-size: 0.9rem;
    font-family: 'Titillium Web', sans-serif;
    min-height: 40px;
  }

  .spinner-dot { width: 8px; height: 8px; background: #1e3a8a; border-radius: 50%; animation: bounce 1.2s infinite ease-in-out; display: inline-block; }
  .spinner-dot:nth-child(2) { animation-delay: 0.2s; }
  .spinner-dot:nth-child(3) { animation-delay: 0.4s; }

  @keyframes bounce {
    0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
    40% { transform: scale(1); opacity: 1; }
  }

  @media (max-width: 768px) {
    .carousel-section h2 { font-size: 1.5rem; }
    .carousel-section p  { font-size: 1rem; margin-bottom: 2rem; }
    .carousel-wrapper { flex-direction: column; align-items: center; position: relative; height: auto; padding-bottom: 300px; }
    .carousel-left { width: 96%; max-width: 320px; height: 272px; border-radius: 1rem; margin: 0 auto; position: absolute; top: 0; left: 50%; transform: translateX(-50%); z-index: 1; }
    .carousel-image { width: 104%; height: 82%; object-fit: cover; }
    .carousel-card { width: 72%; height: 292px; padding: 1rem; border-radius: 1rem; position: absolute; top: 216px; left: 83%; transform: translateX(-50%); z-index: 2; background: #fff; overflow: hidden; }
    .carousel-card h3 { font-size: 1rem; font-weight: 600; margin-bottom: 0.5rem; }
    .carousel-card p  { font-size: 0.9rem; color: #444; display: -webkit-box; -webkit-line-clamp: 4; -webkit-box-orient: vertical; overflow: hidden; text-overflow: ellipsis; }
    .carousel-dots { margin-top: 241px; }
    .news-grid { grid-template-columns: repeat(2, 1fr); column-gap: 0.75rem; row-gap: 1.5rem; }
    .news-card-img-wrap { height: 160px; }
    .news-card img { height: 160px; }
  }

  @media (max-width: 480px) {
    .news-grid { grid-template-columns: 1fr; }
    .news-card-img-wrap { height: 200px; }
    .news-card img { height: 200px; }
  }
`}</style>

      <section className="carousel-section">
        <h2>Latest News & Offers</h2>
        <p>
          Stay updated with the latest credit card offers, insights, and
          financial news.
        </p>

        {isNewsPage ? (
          <>
            <div className="news-grid">
              {/* Initial loading skeletons */}
              {loading ? (
                Array.from({ length: PAGE_SIZE }).map((_, i) => (
                  <SkeletonCard key={`sk-init-${i}`} />
                ))
              ) : (
                <>
                  {/* Loaded cards */}
                  {visibleItems.map((item, idx) => (
                    <NewsCard
                      key={`news-${item.id ?? idx}`}
                      item={item}
                      onClick={() =>
                        navigate(
                          `/offer-and-news/${encodeURIComponent(item.id)}`
                        )
                      }
                    />
                  ))}

                  {/* Skeleton placeholders shown WHILE preloading next batch */}
                  {isPreloading &&
                    Array.from({ length: skeletonCount }).map((_, i) => (
                      <SkeletonCard key={`sk-more-${i}`} />
                    ))}
                </>
              )}

              {!loading && visibleItems.length === 0 && (
                <div style={{ gridColumn: '1/-1', color: '#64748b' }}>
                  No articles available.
                </div>
              )}
            </div>

            {/* Status row below grid */}
            <div className="load-status">
              {isPreloading && (
                <>
                  <span className="spinner-dot" />
                  <span className="spinner-dot" />
                  <span className="spinner-dot" />
                  <span style={{ marginLeft: 4 }}>Loading articles…</span>
                </>
              )}
              {!loading &&
                !isPreloading &&
                !hasMore &&
                visibleItems.length > 0 && (
                  <span>You've seen all articles</span>
                )}
            </div>
          </>
        ) : (
          <>
            <div className="carousel-wrapper">
              <div className="carousel-left">
                {currentItem?.image ? (
                  <img
                    src={currentItem.image}
                    alt={currentItem.title}
                    className="carousel-image"
                  />
                ) : (
                  <div className="carousel-placeholder">
                    {loading ? <div className="skeleton-img" /> : 'No Image'}
                  </div>
                )}
              </div>
              <div className="carousel-card">
                <div>
                  <div className="date">{currentItem?.date || ''}</div>
                  <h3>
                    {currentItem?.title || (loading ? '…' : 'No article')}
                  </h3>
                  <p>{currentItem?.excerpt || ''}</p>
                </div>
                {currentItem && (
                  <button
                    onClick={() =>
                      navigate(
                        `/offer-and-news/${encodeURIComponent(currentItem.id)}`
                      )
                    }
                  >
                    Read More
                  </button>
                )}
              </div>
            </div>
            <div className="carousel-dots">
              {(topFive.length ? topFive : Array.from({ length: 5 })).map(
                (_, idx) => (
                  <div
                    key={`dot-${idx}`}
                    className={`carousel-dot ${idx === current ? 'active' : ''}`}
                    onClick={() => setCurrent(idx)}
                  />
                )
              )}
            </div>
          </>
        )}
      </section>
    </>
  );
}
